import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  brandLabelToValue,
  HandleKindSchema,
  type ApiClientErrorCode,
  type HandleKind,
  type HandleListItem,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useCatalogFilters } from './useCatalogFilters';
import { HandlesCatalogView, type HandlesCatalogState } from './HandlesCatalogView';
import { useHandleImageUrls } from './useHandleImageUrls';

/**
 * Container для Экрана 2.4.2 (см. ADR-0005). Связывает URL-state, локальный
 * state выбора чипов, fetch через apiClient → передаёт данные Presenter'у.
 */
export function HandlesCatalog() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string }>();
  const kindParse = HandleKindSchema.safeParse(params.kind);
  const kind: HandleKind = kindParse.success ? kindParse.data : 'handle';

  const HANDLES_FIELDS = ['colors', 'shapes', 'brands'] as const;
  const { filters: appliedFilters, hasFilters, apply, reset } = useCatalogFilters(HANDLES_FIELDS);
  // noUncheckedIndexedAccess дает T|undefined на generic Record-аксесс.
  // useMemo стабилизирует ссылки для useEffect deps (react-hooks/exhaustive-deps).
  const appliedColors = useMemo(() => appliedFilters.colors ?? [], [appliedFilters.colors]);
  const appliedShapes = useMemo(() => appliedFilters.shapes ?? [], [appliedFilters.shapes]);
  // ADR-0076: в URL/state хранятся МЕТКИ брендов (вкл. «Без бренда»); для API
  // маппим в значения (`brandLabelToValue`: «Без бренда» → '').
  const appliedBrands = useMemo(() => appliedFilters.brands ?? [], [appliedFilters.brands]);

  // Preview-state чипов (до клика «Найти»). Инициализируется applied-фильтрами
  // для поддержки deep-link.
  const [selectedBrands, setSelectedBrands] = useState<readonly string[]>(appliedBrands);
  const [selectedColors, setSelectedColors] = useState<readonly string[]>(appliedColors);
  const [selectedShapes, setSelectedShapes] = useState<readonly string[]>(appliedShapes);

  // ADR-0073: аккумулируем страницы keyset-пагинации. `items` — накопленный список,
  // `nextCursor` — opaque-курсор следующей страницы (null = последняя). Курсор НЕ в
  // URL (Type C, transient): «Показать ещё» — состояние компонента.
  const [items, setItems] = useState<HandleListItem[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // первичная загрузка (страница 1)
  const [loadingMore, setLoadingMore] = useState(false); // дозагрузка «Показать ещё»
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);
  // Монотонный счётчик загрузок: гасит устаревшие ответы при смене фильтра.
  const loadSeqRef = useRef(0);

  // JSON.stringify — простейший способ depend на массивах для useEffect (Phase 5 MVP).
  // Когда появится TanStack Query (Phase 7+) — заменим на useQuery с автоматическим dedup.
  const filtersKey = JSON.stringify(appliedFilters);
  // ADR-0076: метки брендов → значения API («Без бренда» → ''). Стабилизируем для deps.
  const brandsForApi = useMemo(() => appliedBrands.map(brandLabelToValue), [appliedBrands]);

  const toApiError = (err: unknown): { code: ApiClientErrorCode; message: string } =>
    // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
    err instanceof ApiClientError
      ? { code: err.code, message: err.message }
      : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };

  // Страница 1 при смене фильтра/вида — сбрасывает накопленный список и курсор.
  useEffect(() => {
    if (!hasFilters) {
      setItems(null);
      setNextCursor(null);
      setError(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError(null);
    setLoadingMore(false);
    apiClient.handles
      .list({ kind, brands: brandsForApi, colors: [...appliedColors], shapes: [...appliedShapes] })
      .then(
        (res) => {
          if (loadSeqRef.current !== seq) return;
          setItems(res.items);
          setNextCursor(res.nextCursor);
          setLoading(false);
        },
        (err: unknown) => {
          if (loadSeqRef.current !== seq) return;
          setError(toApiError(err));
          setItems(null);
          setNextCursor(null);
          setLoading(false);
        },
      );
  }, [kind, hasFilters, filtersKey, brandsForApi, appliedColors, appliedShapes]);

  // «Показать ещё»: дозагрузка следующей keyset-страницы, append к накопленному.
  const onLoadMore = () => {
    if (nextCursor === null || loadingMore) return;
    const seq = loadSeqRef.current;
    setLoadingMore(true);
    apiClient.handles
      .list({
        kind,
        brands: brandsForApi,
        colors: [...appliedColors],
        shapes: [...appliedShapes],
        cursor: nextCursor,
      })
      .then(
        (res) => {
          if (loadSeqRef.current !== seq) return; // фильтр сменился — ответ устарел
          setItems((prev) => [...(prev ?? []), ...res.items]);
          setNextCursor(res.nextCursor);
          setLoadingMore(false);
        },
        () => {
          if (loadSeqRef.current !== seq) return;
          setLoadingMore(false); // дозагрузка не ломает success-список
        },
      );
  };

  const state = useMemo<HandlesCatalogState>(() => {
    if (error) return 'error';
    if (loading) return 'loading';
    if (items === null) return 'idle';
    if (items.length === 0) return 'empty';
    return 'success';
  }, [error, loading, items]);

  // Ф-Фронт (ADR-0070): signed-URL для фото видимых карточек (ключ = primaryPhotoKey).
  const photoKeys = useMemo(() => (items ?? []).map((item) => item.primaryPhotoKey), [items]);
  const { urls: imageUrls } = useHandleImageUrls(photoKeys);

  const onChipBrandToggle = (value: string) =>
    setSelectedBrands((prev) =>
      prev.includes(value) ? prev.filter((b) => b !== value) : [...prev, value],
    );

  const onChipColorToggle = (value: string) =>
    setSelectedColors((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );

  const onChipShapeToggle = (value: string) =>
    setSelectedShapes((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    );

  const onSearch = () =>
    apply({
      brands: [...selectedBrands],
      colors: [...selectedColors],
      shapes: [...selectedShapes],
    });

  const onResetAll = () => {
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedShapes([]);
    reset();
  };

  return (
    <HandlesCatalogView
      kind={kind}
      state={state}
      items={items ?? undefined}
      imageUrls={imageUrls}
      hasMore={nextCursor !== null}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      error={error ?? undefined}
      selectedBrands={selectedBrands}
      selectedColors={selectedColors}
      selectedShapes={selectedShapes}
      onChipBrandToggle={onChipBrandToggle}
      onChipColorToggle={onChipColorToggle}
      onChipShapeToggle={onChipShapeToggle}
      onResetBrands={() => setSelectedBrands([])}
      onResetColors={() => setSelectedColors([])}
      onResetShapes={() => setSelectedShapes([])}
      onSearch={onSearch}
      onResetAll={onResetAll}
      onItemClick={(globalId) => navigate(`/handles/${kind}/${encodeURIComponent(globalId)}`)}
    />
  );
}
